import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/ui/utils/tailwind';
import { StatusBadge, Text } from '@/shared/ui';
import Button from '@/shared/ui/components/basic/Button';
import type { Launch } from '../types';
import {
  ALLOCATION_STATUS_COLOR,
  LAUNCH_LIFECYCLE_BADGE,
} from './launch-unit-status';
import { formatLocalizedDate } from '@/shared/utils/date';

interface LaunchCardProps {
  launch: Launch;
  onClick: () => void;
  canManage?: boolean;
  onPublish?: () => void;
  onClose?: () => void;
}

/**
 * Tallies unit allocation statuses into a frequency map.
 */
function _countByStatus(units: Launch['units']): Record<string, number> {
  return (units ?? []).reduce<Record<string, number>>((acc, u) => {
    acc[u.allocation_status] = (acc[u.allocation_status] || 0) + 1;
    return acc;
  }, {});
}

/**
 * Derives aggregate unit allocation counts for the launch card KPIs.
 */
function unitCounts(launch: Launch) {
  const counts = _countByStatus(launch.units);
  return {
    available: counts['available'] || 0,
    held: counts['held'] || 0,
    reserved: counts['reserved_with_deposit'] || 0,
    sold: counts['sold'] || 0,
    total: (launch.units ?? []).length,
  };
}

/**
 * Renders a stacked horizontal progress bar for unit allocation.
 */
function ProgressBar({ sold, reserved, held, total }: {
  sold: number; reserved: number; held: number; total: number;
}) {
  if (total === 0) return null;
  const soldPct = (sold / total) * 100;
  const reservedPct = (reserved / total) * 100;
  const heldPct = (held / total) * 100;

  return (
    <div className="flex h-[var(--space-1)] w-full overflow-hidden rounded-(--radius-sm) bg-(--border-ultra-subtle)">
      <div style={{ width: `${soldPct}%`, background: 'var(--launch-status-sold-solid)' }} />
      <div style={{ width: `${reservedPct}%`, background: 'var(--launch-status-reserved-solid)' }} />
      <div style={{ width: `${heldPct}%`, background: 'var(--launch-status-held-solid)' }} />
    </div>
  );
}

/**
 * Displays a single KPI value with a colored label beneath.
 */
function Metric({ value, label, color }: { value: number; label: string; color: string }) {
  return (
    <div className="flex flex-col items-center">
      <Text as="span" className="text-[length:var(--text-app-heading)] font-[var(--font-medium)] tabular-nums" style={{ color }}>
        {value}
      </Text>
      <Text variant="meta" as="span" className="text-[var(--text-tertiary)]">{label}</Text>
    </div>
  );
}

/**
 * Card displaying a launch with progress bar, unit metrics, and status badge.
 */
export function LaunchCard({ launch, onClick, canManage, onPublish, onClose }: LaunchCardProps) {
  const { t } = useTranslation('features/launches');
  const counts = unitCounts(launch);

  return (
    // Not a <button>: this card carries its own action button ("publicar",
    // "fechar"), and a button inside a button is invalid HTML that React
    // reports as a hydration error. A div with the button role keeps the
    // whole card clickable and keyboard-reachable without nesting.
    <div
      data-tour="launch-card"
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      className={cn(
        'flex flex-col gap-[var(--space-4)] rounded-(--radius-lg) text-left',
        'border border-(--border-subtle) bg-(--surface-card)',
        'p-[var(--space-5)] cursor-pointer w-full',
        'hover:border-(--border-medium)',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--seed-base)]',
      )}
      style={{ transition: 'var(--transition-standard)' }}
    >
      <div className="flex items-start justify-between gap-[var(--space-3)]">
        <div className="flex flex-col gap-[var(--space-1)] min-w-0">
          <Text as="span" className="text-[length:var(--text-app-section)] font-[var(--font-semibold)] text-(--text-primary) leading-[var(--leading-tight)] truncate">
            {launch.name}
          </Text>
          {launch.address && (
            <Text variant="meta" as="span" className="text-(--text-tertiary) truncate">
              {launch.address}
            </Text>
          )}
        </div>
        <StatusBadge className={LAUNCH_LIFECYCLE_BADGE[launch.status]}>
          {t(`launchStatus.${launch.status}`)}
        </StatusBadge>
      </div>

      <ProgressBar sold={counts.sold} reserved={counts.reserved} held={counts.held} total={counts.total} />

      <div className="grid grid-cols-4 gap-[var(--space-2)]">
        <Metric value={counts.available} label={t('status.available')} color={ALLOCATION_STATUS_COLOR.available} />
        <Metric value={counts.held} label={t('status.held')} color={ALLOCATION_STATUS_COLOR.held} />
        <Metric value={counts.reserved} label={t('status.reserved_with_deposit')} color={ALLOCATION_STATUS_COLOR.reserved_with_deposit} />
        <Metric value={counts.sold} label={t('status.sold')} color={ALLOCATION_STATUS_COLOR.sold} />
      </div>

      <div className="flex items-center justify-between border-t border-(--border-ultra-subtle) pt-[var(--space-3)]">
        <Text variant="small" as="span" className="text-(--text-tertiary)">
          {t('list.unitCount', { count: counts.total })}
        </Text>
        {canManage && launch.status === 'draft' && onPublish ? (
          <Button
            data-testid={`publish-launch-${launch.launch_id}`}
            variant="outline"
            size="sm"
            onClick={(e) => { e.stopPropagation(); onPublish(); }}
          >
            {t('list.publish')}
          </Button>
        ) : canManage && launch.status === 'open' && onClose ? (
          <Button
            data-testid={`close-launch-${launch.launch_id}`}
            variant="outline"
            size="sm"
            onClick={(e) => { e.stopPropagation(); onClose(); }}
          >
            {t('board.close')}
          </Button>
        ) : launch.opened_at ? (
          <Text variant="meta" as="span" className="text-(--text-tertiary)">
            {t('board.openedOn', { date: formatLocalizedDate(launch.opened_at) })}
          </Text>
        ) : null}
      </div>
    </div>
  );
}
